import type { UseFormSetError, Path, FieldValues } from "react-hook-form";
import type { ApiError } from "@/lib/api";

/**
 * Parsea errores 422 de Pydantic/FastAPI y los aplica al form con `setError`.
 *
 * Formato de `details` (Pydantic v2):
 *   [
 *     { "loc": ["body", "rfc"], "msg": "field required", "type": "missing" },
 *     { "loc": ["body", "contactos", 0, "email"], "msg": "...", "type": "value_error.email" }
 *   ]
 *
 * Convierte `loc` a path dot-notation de RHF:
 *   ["body", "rfc"]                           → "rfc"
 *   ["body", "contactos", 0, "email"]         → "contactos.0.email"
 *
 * Retorna la lista de labels de campos con error (para el banner superior).
 */

type PydanticItem = {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
};

const MSG_TRANSLATIONS: Record<string, string> = {
  "field required": "Este campo es obligatorio",
  "value is not a valid email address": "Email no válido",
  "string too short": "Muy corto",
  "string too long": "Muy largo",
  "value is not a valid uuid": "Identificador inválido",
  "input should be a valid string": "Debe ser texto",
  "input should be a valid integer": "Debe ser número entero",
  "input should be a valid number": "Debe ser número",
  "input should be a valid boolean": "Debe ser sí/no",
};

function translateMsg(msg?: string): string {
  if (!msg) return "Valor inválido";
  const lower = msg.toLowerCase();
  for (const [en, es] of Object.entries(MSG_TRANSLATIONS)) {
    if (lower.includes(en.toLowerCase())) return es;
  }
  return msg;
}

function locToPath(loc: (string | number)[]): string {
  // Descarta prefijos comunes de FastAPI ("body", "query", "path").
  const parts = loc.filter(
    (p, idx) => !(idx === 0 && typeof p === "string" && ["body", "query", "path"].includes(p))
  );
  return parts.map(String).join(".");
}

export function applyPydanticErrors<T extends FieldValues>(
  err: ApiError,
  setError: UseFormSetError<T>
): string[] {
  if (err.status !== 422 || !Array.isArray(err.details)) return [];

  const affected: string[] = [];
  for (const item of err.details as PydanticItem[]) {
    if (!item.loc) continue;
    const path = locToPath(item.loc);
    if (!path) continue;
    const message = translateMsg(item.msg);
    try {
      setError(path as Path<T>, { type: "server", message });
      affected.push(path);
    } catch {
      /* path inválido para el form — ignoramos */
    }
  }
  return affected;
}

/**
 * Dado un objeto `errors` de RHF, devuelve lista plana de mensajes
 * "campo: mensaje" para banner superior.
 */
export function flattenRhfErrors(
  errors: Record<string, unknown>,
  prefix = ""
): { path: string; message: string }[] {
  const out: { path: string; message: string }[] = [];
  for (const [k, v] of Object.entries(errors)) {
    if (!v) continue;
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "object" && v !== null) {
      if ("message" in v && typeof (v as { message?: unknown }).message === "string") {
        out.push({ path: key, message: (v as { message: string }).message });
      } else {
        out.push(...flattenRhfErrors(v as Record<string, unknown>, key));
      }
    }
  }
  return out;
}

/**
 * Mapa de labels legibles por path. Usado en el banner de errores
 * para mostrar "Razón social" en vez de "razon_social".
 */
export const FIELD_LABELS: Record<string, string> = {
  // Cliente
  rfc: "RFC",
  razon_social: "Razón social",
  nombre_comercial: "Nombre comercial",
  tipo: "Tipo de cliente",
  curp: "CURP",
  "datos_fiscales.email_fiscal": "Email fiscal",
  "datos_fiscales.regimen_fiscal": "Régimen fiscal",
  "datos_fiscales.codigo_postal": "Código postal",
  "direccion_instalacion.calle": "Calle",
  "direccion_instalacion.codigo_postal": "CP instalación",
  "contactos.nombre": "Nombre del contacto",
  "contactos.email": "Email del contacto",
  "contactos.telefono": "Teléfono",
  // Contrato
  cliente_id: "Cliente",
  plan_id: "Plan",
  router_id: "Router",
  red_id: "Red",
  ip_asignada: "IP asignada",
  pppoe_user: "Usuario PPPoE",
  pppoe_password: "Password PPPoE",
  fecha_corte: "Día de corte",
  precio_override: "Precio override",
  descuento_pct: "% descuento",
  "wireless_detalle.cpe_mac": "MAC del CPE",
  "fiber_detalle.onu_mac": "MAC de la ONU",
};

export function labelForPath(path: string): string {
  // Intenta match exacto, luego quitando índices numéricos.
  if (FIELD_LABELS[path]) return FIELD_LABELS[path];
  const generic = path.replace(/\.\d+\./g, ".");
  if (FIELD_LABELS[generic]) return FIELD_LABELS[generic];
  return path;
}
